import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiPost } from "../api";
import { useAuth } from "../auth/AuthProvider";
import ExternalButton from "../components/BaseButton/ExternalButton";
import CodeWriter from "../components/CodeWriter";
import MailWriter from "../components/MailWriter";
import MainLogo from "../components/MainLogo.jsx";
import { useAppData } from "../data/AppDataProvider";
import { clearStoredReferral, getStoredReferralPayload } from "../referralStorage";
import "../index.css";

const RESEND_TIMEOUT_SECONDS = 60;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const TELEGRAM_BOT_URL = "https://t.me/sodavpn_bot";

let googleScriptPromise;

function loadGoogleIdentityScript() {
    if (window.google?.accounts?.id) {
        return Promise.resolve(window.google);
    }

    if (!googleScriptPromise) {
        googleScriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = GOOGLE_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve(window.google);
            script.onerror = () => reject(new Error("Не удалось загрузить Google Identity Services"));
            document.head.appendChild(script);
        });
    }

    return googleScriptPromise;
}

function getErrorMessage(error, fallbackMessage) {
    if (!error?.message) {
        return fallbackMessage;
    }

    try {
        const parsed = JSON.parse(error.message);
        if (typeof parsed?.detail === "string" && parsed.detail.trim()) {
            return parsed.detail;
        }
    } catch {
        return error.message;
    }

    return fallbackMessage;
}

function maskEmail(value) {
    const [localPart = "", domain = ""] = value.split("@");

    if (!localPart || !domain) {
        return value;
    }

    const visibleLocal = localPart.slice(0, 4);
    const maskedDomain = domain.length > 2
        ? `${domain.slice(0, 2)}****${domain.slice(-2)}`
        : `${domain[0]}****`;

    return `${visibleLocal}****${maskedDomain}`;
}

export default function Auth({ mode = "login" }) {
    const auth = useAuth();
    const navigate = useNavigate();
    const { refreshCabinetData } = useAppData();
    const lastSubmittedCodeRef = useRef("");
    const googleButtonRef = useRef(null);
    const isLinkMode = mode === "link";
    const isTelegramLoginScreen = auth.isTelegram && !isLinkMode;

    const [step, setStep] = useState("email");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [isRequestLoading, setIsRequestLoading] = useState(false);
    const [isVerifyLoading, setIsVerifyLoading] = useState(false);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [resendCooldown, setResendCooldown] = useState(0);

    const getAppearStyle = () => ({ "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s` });

    const getAuthPayload = useCallback((payload) => {
        if (isLinkMode) {
            return payload;
        }

        const referralPayload = getStoredReferralPayload();
        return {
            ...payload,
            ...(referralPayload.ref_id ? { ref_id: referralPayload.ref_id } : {}),
            ...(referralPayload.source ? { source: referralPayload.source } : {}),
        };
    }, [isLinkMode]);

    const telegramAuthUrl = React.useMemo(() => {
        const referralPayload = getStoredReferralPayload();

        if (!referralPayload.ref_id) {
            return TELEGRAM_BOT_URL;
        }

        return `${TELEGRAM_BOT_URL}?start=${encodeURIComponent(referralPayload.ref_id)}`;
    }, []);

    const completeAuth = useCallback((accessToken) => {
        auth.login(accessToken);
        clearStoredReferral();
        navigate("/", { replace: true });
    }, [auth, navigate]);

    const completeLink = useCallback(async () => {
        await refreshCabinetData();
        navigate("/cabinet", { replace: true });
    }, [navigate, refreshCabinetData]);

    async function requestCode(event) {
        event?.preventDefault();

        try {
            setIsRequestLoading(true);
            setError("");

            await apiPost("/cabinet/auth/request-code", { email });
            setStep("code");
            setCode("");
            setInfo(`Код подтверждения отправлен\nПочта ${maskEmail(email)}`);
            setResendCooldown(RESEND_TIMEOUT_SECONDS);
            lastSubmittedCodeRef.current = "";
        } catch (requestError) {
            setError(getErrorMessage(requestError, "Не удалось отправить код"));
        } finally {
            setIsRequestLoading(false);
        }
    }

    const verifyCode = useCallback(async (value) => {
        try {
            setIsVerifyLoading(true);
            setError("");

            const response = await apiPost(
                isLinkMode ? "/cabinet/auth/link-email" : "/cabinet/auth/verify-code",
                getAuthPayload({ email, code: value }),
                isLinkMode ? auth : undefined,
            );

            if (isLinkMode) {
                setInfo("Почта успешно привязана");
                await completeLink();
                return;
            }

            completeAuth(response.access_token);
        } catch (requestError) {
            setError(getErrorMessage(requestError, "Неверный код или истек срок действия"));
        } finally {
            setIsVerifyLoading(false);
        }
    }, [auth, completeAuth, completeLink, email, getAuthPayload, isLinkMode]);

    const handleGoogleCredentialResponse = useCallback(async (response) => {
        if (!response?.credential) {
            setError("Google не вернул токен авторизации");
            return;
        }

        try {
            setError("");

            const authResponse = await apiPost(
                isLinkMode ? "/cabinet/auth/link-google" : "/cabinet/auth/google",
                getAuthPayload({ id_token: response.credential }),
                isLinkMode ? auth : undefined,
            );

            if (isLinkMode) {
                await completeLink();
                return;
            }

            completeAuth(authResponse.access_token);
        } catch (requestError) {
            setError(getErrorMessage(requestError, "Ошибка авторизации через Google"));
        }
    }, [auth, completeAuth, completeLink, getAuthPayload, isLinkMode]);

    function handleBackToEmail() {
        setStep("email");
        setCode("");
        setError("");
        setInfo("");
        setResendCooldown(0);
        lastSubmittedCodeRef.current = "";
    }

    function handleCodeChange(value) {
        setCode(value);
        setError("");

        if (value.length < 6) {
            lastSubmittedCodeRef.current = "";
        }
    }

    useEffect(() => {
        if (step !== "code" || resendCooldown <= 0) {
            return undefined;
        }

        const timerId = window.setInterval(() => {
            setResendCooldown((currentValue) => {
                if (currentValue <= 1) {
                    window.clearInterval(timerId);
                    return 0;
                }

                return currentValue - 1;
            });
        }, 1000);

        return () => window.clearInterval(timerId);
    }, [step, resendCooldown]);

    useEffect(() => {
        if (step !== "code" || code.length !== 6 || isVerifyLoading) {
            return;
        }

        if (lastSubmittedCodeRef.current === code) {
            return;
        }

        lastSubmittedCodeRef.current = code;
        void verifyCode(code);
    }, [code, isVerifyLoading, step, verifyCode]);

    useEffect(() => {
        if (isTelegramLoginScreen || !GOOGLE_CLIENT_ID || !googleButtonRef.current) {
            return undefined;
        }

        let isMounted = true;

        loadGoogleIdentityScript()
            .then((google) => {
                if (!isMounted || !google?.accounts?.id || !googleButtonRef.current) {
                    return;
                }

                const buttonWidth = googleButtonRef.current.clientWidth || 0;

                google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleCredentialResponse,
                });

                googleButtonRef.current.innerHTML = "";
                google.accounts.id.renderButton(googleButtonRef.current, {
                    type: "standard",
                    theme: "outline",
                    size: "large",
                    text: "signin_with",
                    shape: "pill",
                    width: buttonWidth,
                    logo_alignment: "left",
                });
            })
            .catch((loadError) => {
                if (isMounted) {
                    setError(getErrorMessage(loadError, "Не удалось загрузить авторизацию Google"));
                }
            });

        return () => {
            isMounted = false;
        };
    }, [handleGoogleCredentialResponse, isTelegramLoginScreen]);

    if (isTelegramLoginScreen) {
        return (
            <>
                <div className="flex-center h100">
                    <MainLogo />
                </div>
                <div className="flex-col gap1 menu-appear" style={getAppearStyle()}>
                    {auth.isAuthenticatingTelegram ? (
                        <div className="gray-block pad1 shadow-1">Выполняется вход через Telegram...</div>
                    ) : (
                        <div className="attention-block pad1 shadow-1">
                            Не удалось выполнить вход через Telegram. Откройте приложение заново из бота.
                        </div>
                    )}
                </div>
            </>
        );
    }

    return (
        <>
            {!isLinkMode ? (
                <div className="flex-center h100">
                    <MainLogo />
                </div>
            ) : (
                <div className="flex-col gap05 menu-appear" style={getAppearStyle()}>
                    <h2>Привязка почты</h2>
                </div>
            )}
            {error ? <div className="attention-block pad1 shadow-1 menu-appear" style={getAppearStyle()}>{error}</div> : null}
            <div className="flex-col gap1 menu-appear" style={getAppearStyle()}>
                {step === "email" ? (
                    <MailWriter
                        email={email}
                        onEmailChange={setEmail}
                        onSubmit={requestCode}
                        disabled={isRequestLoading}
                        isSubmitting={isRequestLoading}
                    />
                ) : (
                    <CodeWriter
                        code={code}
                        info={info}
                        resendCooldown={resendCooldown}
                        onCodeChange={handleCodeChange}
                        onBack={handleBackToEmail}
                        onResend={requestCode}
                        disabled={isRequestLoading || isVerifyLoading}
                    />
                )}

                <div className="flex-center align-center menu-appear" style={getAppearStyle()}>
                    <span className="line-3 grey" style={{ marginRight: 10 }}></span>
                    <label>или</label>
                    <span className="line-3 grey" style={{ marginLeft: 10 }}></span>
                </div>

                <div className="menu-appear" style={{ ...getAppearStyle(), width: "100%" }}>
                    <div
                        ref={googleButtonRef}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            minHeight: 44,
                        }}
                    />
                </div>

                {!isLinkMode ? (
                    <ExternalButton
                        icon="icon-telegram"
                        href={telegramAuthUrl}
                        className="menu-appear"
                        showArrow={false}
                        contentAlign="center"
                        style={getAppearStyle()}
                    >
                        <span>Войти с помощью Telegram</span>
                    </ExternalButton>
                ) : null}
            </div>
        </>
    );
}
